import React from "react";
import Link from "next/link";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import {
  AdjustmentsHorizontalIcon,
  ClockIcon,
  BellAlertIcon,
  ChartBarIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { docsColors, docsComponents } from "@/lib/docs-theme";

export default function BestPracticesPage() {
  const breadcrumbs = [{ label: "Documentation", href: "/docs" }, { label: "Best Practices" }];

  return (
    <DocLayout
      title="Best Practices - Uptime Monitor Documentation"
      description="Learn how to optimize your Uptime Monitor setup for effective monitoring, cost efficiency, and reduced alert fatigue."
      canonical="https://uptime-monitor.io/docs/best-practices"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Best Practices"
        description="Get the most out of Uptime Monitor with optimal configuration settings and monitoring strategies."
      />

      {/* Fail Threshold Configuration */}
      <DocSection>
        <DocCard>
          <div className="flex items-start">
            <AdjustmentsHorizontalIcon
              className={`h-6 w-6 ${docsColors.icon.primary} mt-1 mr-4 flex-shrink-0`}
            />
            <div>
              <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
                Configure Fail Thresholds
              </h2>
              <p className={`${docsColors.text.body} mb-4`}>
                Uptime Monitor uses a fail threshold algorithm to prevent alerts from temporary
                network glitches. Set the number of consecutive failures required before alerting.
              </p>

              <DocCodeBlock className="mb-4">
                <h3 className={`font-medium ${docsColors.text.heading} mb-3`}>
                  Recommended Settings:
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>Threshold 1:</span>{" "}
                    Not recommended - even AWS network has occasional blips
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>Threshold 2-3:</span>{" "}
                    Good balance for most services (recommended)
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>Threshold 4+:</span>{" "}
                    For services with known intermittent issues
                  </li>
                </ul>
              </DocCodeBlock>

              <DocAlert variant="info">
                <p className={`text-sm ${docsColors.text.body}`}>
                  <strong className={docsColors.text.heading}>Example:</strong> With threshold 2 and
                  10-second checks, you&apos;ll detect downtime in 10-20 seconds.
                </p>
              </DocAlert>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Check Interval Optimization */}
      <DocSection>
        <DocCard>
          <div className="flex items-start">
            <ClockIcon className={`h-6 w-6 ${docsColors.icon.primary} mt-1 mr-4 flex-shrink-0`} />
            <div>
              <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
                Optimize Check Intervals
              </h2>
              <p className={`${docsColors.text.body} mb-4`}>
                Balance between quick detection and resource usage by setting appropriate check
                intervals.
              </p>

              <DocCodeBlock className="mb-4">
                <h3 className={`font-medium ${docsColors.text.heading} mb-3`}>
                  Interval Guidelines:
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>10-30 seconds:</span>{" "}
                    Critical services requiring fastest detection
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>1 minute:</span>{" "}
                    Important production services
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>2-5 minutes:</span>{" "}
                    Standard services
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>5-15 minutes:</span>{" "}
                    Low priority (internal tools, docs)
                  </li>
                </ul>
              </DocCodeBlock>

              <DocCodeBlock>
                <h3 className={`font-medium ${docsColors.text.heading} mb-3`}>
                  Check Frequency Impact:
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>
                      10-second checks:
                    </span>{" "}
                    8,640 checks per day (fastest detection)
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>
                      30-second checks:
                    </span>{" "}
                    2,880 checks per day
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>
                      1-minute checks:
                    </span>{" "}
                    1,440 checks per day
                  </li>
                  <li className={docsColors.text.body}>
                    <span className={`${docsColors.text.heading} font-medium`}>
                      5-minute checks:
                    </span>{" "}
                    288 checks per day
                  </li>
                </ul>
              </DocCodeBlock>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Notification Channels */}
      <DocSection>
        <DocCard>
          <div className="flex items-start">
            <BellAlertIcon className="h-6 w-6 text-green-500 mt-1 mr-4 flex-shrink-0" />
            <div>
              <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
                Configure Notification Channels
              </h2>
              <p className={`${docsColors.text.body} mb-4`}>
                Set up different notification methods based on severity and avoid channel
                limitations:
              </p>

              <ul className={`space-y-2 ${docsColors.text.body}`}>
                <li className="flex items-start">
                  <span className={`${docsColors.icon.success} mr-2`}>•</span>
                  <strong className={`${docsColors.text.heading} pr-1`}>Critical alerts:</strong>
                  PagerDuty, incident.io
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.icon.success} mr-2`}>•</span>
                  <strong className={`${docsColors.text.heading} pr-1`}>
                    Important alerts:
                  </strong>{" "}
                  Slack, Discord, webhooks
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.icon.success} mr-2`}>•</span>
                  <strong className={`${docsColors.text.heading} pr-1`}>Informational:</strong>{" "}
                  Email notifications
                </li>
              </ul>

              <DocAlert variant="warning" className="mt-4">
                <p className="text-sm">
                  <strong>Tip:</strong> Use unlimited alert delivery methods (webhooks, Slack,
                  Discord) instead of credit-based ones (email) to ensure you never miss critical
                  alerts.
                </p>
              </DocAlert>

              <DocAlert variant="info" className="mt-4">
                <p className="text-sm">
                  <strong>Test Your Contacts:</strong> Use the Test button next to each contact in
                  Settings → Contacts to verify notification delivery is working before you need it.
                </p>
              </DocAlert>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Dashboard Best Practices */}
      <DocSection>
        <DocCard>
          <div className="flex items-start">
            <ChartBarIcon
              className={`h-6 w-6 ${docsColors.icon.primary} mt-1 mr-4 flex-shrink-0`}
            />
            <div>
              <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
                Dashboard Best Practices
              </h2>

              <ul className={`space-y-2 ${docsColors.text.body}`}>
                <li className="flex items-start">
                  <CheckCircleIcon className={`h-4 w-4 ${docsColors.icon.success} mt-0.5 mr-2`} />
                  Delete or pause monitors that are no longer needed
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className={`h-4 w-4 ${docsColors.icon.success} mt-0.5 mr-2`} />
                  Use consistent naming conventions for monitors
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className={`h-4 w-4 ${docsColors.icon.success} mt-0.5 mr-2`} />
                  Use descriptive names for monitors
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className={`h-4 w-4 ${docsColors.icon.success} mt-0.5 mr-2`} />
                  Set appropriate timeout values (3-10 seconds)
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className={`h-4 w-4 ${docsColors.icon.success} mt-0.5 mr-2`} />
                  Use status pages to group related services
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className={`h-4 w-4 ${docsColors.icon.success} mt-0.5 mr-2`} />
                  Pause monitors during maintenance windows
                </li>
              </ul>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Quick Optimization Checklist */}
      <DocSection title="Quick Optimization Checklist">
        <DocAlert variant="success" className="p-6">
          <ul className="space-y-3">
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <span className={docsColors.text.body}>
                Set fail threshold to 2-3 for most monitors
              </span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <span className={docsColors.text.body}>
                Use 10-second to 1-minute checks for critical services for fastest downtime
                detection
              </span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <span className={docsColors.text.body}>
                Configure multiple notification channels for redundancy
              </span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <span className={docsColors.text.body}>
                Use unlimited alert delivery methods (webhooks, Slack, Discord) instead of limited
                ones (email)
              </span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <span className={docsColors.text.body}>
                Pause or delete monitors for deprecated services
              </span>
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Next Steps */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <p className={`${docsColors.text.body} mb-4`}>Ready to optimize your monitoring setup?</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              href="/dashboard/monitors"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
            >
              Go to Monitors
            </Link>
            <Link
              href="/dashboard/contacts"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
            >
              Manage Contacts
            </Link>
          </div>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/status-pages", text: "Status Pages" }}
        next={{ href: "/docs/troubleshooting", text: "Troubleshooting" }}
      />
    </DocLayout>
  );
}
